'use client';

import { Suspense, useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { UiIcon } from '@/components/ui/UiIcon';
import { useRouteModalOptional } from '@/components/ui/patterns/UiRouteModal';
import { DynamicForm, type PrivacyPolicyLink } from '@/features/forms';
import { cn } from '@/lib/utils/tw-merge';
import type { TabFormPanel } from './tabForm.types';

/** Arrow keys move along the tabs and wrap at the ends; Home and End jump. */
function nextTabIndex(key: string, index: number, count: number): number | null {
    switch (key) {
        case 'ArrowRight':
            return (index + 1) % count;
        case 'ArrowLeft':
            return (index - 1 + count) % count;
        case 'Home':
            return 0;
        case 'End':
            return count - 1;
        default:
            return null;
    }
}

/** The tab a hash names: its first segment, so `#service&leistung=reparatur`
 *  selects the tab and leaves the pairs to the form's preselect. */
function tabIdFromHash(hash: string): string {
    return decodeURIComponent(hash.slice(1).split('&')[0]);
}

/** Client tabs: the active tab comes from the URL hash (= the tab's id) and can
 *  be switched by click or arrow keys. Deep links — including ones from the
 *  navigation or the footer — select the matching tab; on a page they also
 *  scroll to the block. */
export function TabFormTabs({
    panels,
    privacyPolicyPage,
    centered = false,
}: {
    panels: TabFormPanel[];
    privacyPolicyPage?: PrivacyPolicyLink;
    centered?: boolean;
}) {
    const rootRef = useRef<HTMLDivElement>(null);
    // A dialog opens on its content, so only a page scrolls to the block.
    const inModal = useRouteModalOptional() !== null;
    const [active, setActive] = useState(() => panels[0]?.id ?? '');

    const scrollToBlock = useCallback(() => {
        if (inModal) return;
        const section = rootRef.current?.closest('section');
        if (!section) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        section.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }, [inModal]);

    // Stable effect trigger instead of the `panels` reference, which is new on
    // every render.
    const formIds = panels.map((panel) => panel.id).join('|');

    useEffect(() => {
        const ids = new Set(formIds.split('|'));

        const activateFromHash = () => {
            const tabId = tabIdFromHash(window.location.hash);
            if (tabId && ids.has(tabId)) {
                setActive(tabId);
                scrollToBlock();
            }
        };

        activateFromHash();

        window.addEventListener('hashchange', activateFromHash);
        window.addEventListener('popstate', activateFromHash);

        // A Next.js <Link> to the same route navigates through history.pushState
        // and fires no hashchange, so catch those clicks in the capture phase and
        // read the tab straight off the link target.
        const onDocumentClick = (event: MouseEvent) => {
            const link = (event.target as Element | null)?.closest('a[href]');
            if (!(link instanceof HTMLAnchorElement)) return;
            if (link.pathname !== window.location.pathname) return;
            const targetId = tabIdFromHash(link.hash);
            if (targetId && ids.has(targetId)) {
                setActive(targetId);
                requestAnimationFrame(scrollToBlock);
            }
        };
        document.addEventListener('click', onDocumentClick, true);

        return () => {
            window.removeEventListener('hashchange', activateFromHash);
            window.removeEventListener('popstate', activateFromHash);
            document.removeEventListener('click', onDocumentClick, true);
        };
    }, [formIds, scrollToBlock]);

    // replaceState rather than assigning the hash — that would scroll again and
    // fire a second hashchange.
    const selectTab = (id: string) => {
        setActive(id);
        history.replaceState(null, '', `#${id}`);
    };

    // Roving focus: only the selected tab is in the tab order, the arrow keys
    // select and focus its neighbours.
    const onTabListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const current = panels.findIndex((panel) => panel.id === active);
        const next = nextTabIndex(event.key, Math.max(current, 0), panels.length);
        if (next === null) return;

        event.preventDefault();
        const { id } = panels[next];
        selectTab(id);
        event.currentTarget.querySelector<HTMLElement>(`#tab-${CSS.escape(id)}`)?.focus();
    };

    return (
        <div ref={rootRef} className={cn('max-w-page-2xs', centered && 'mx-auto')}>
            <div className={cn('form-tabs-wrap', centered && 'flex justify-center')}>
                <div role="tablist" aria-label="Formulare" className="form-tabs" onKeyDown={onTabListKeyDown}>
                    {panels.map((panel) => {
                        const selected = panel.id === active;

                        return (
                            <button
                                key={panel.id}
                                id={`tab-${panel.id}`}
                                type="button"
                                role="tab"
                                aria-selected={selected}
                                aria-controls={`panel-${panel.id}`}
                                tabIndex={selected ? 0 : -1}
                                onClick={() => selectTab(panel.id)}
                                className="form-tab"
                            >
                                {panel.icon ? <UiIcon name={panel.icon} size={18} aria-hidden="true" /> : null}
                                {/* Only one label is displayed at a time, so the name is always the visible text. */}
                                <span className="form-tab-label">{panel.title}</span>
                                {panel.shortTitle ? <span className="form-tab-short">{panel.shortTitle}</span> : null}
                            </button>
                        );
                    })}
                </div>
            </div>

            {panels.map((panel) => (
                <div
                    key={panel.id}
                    role="tabpanel"
                    id={`panel-${panel.id}`}
                    aria-labelledby={`tab-${panel.id}`}
                    hidden={panel.id !== active}
                >
                    {/* An embed panel arrives rendered from the server. */}
                    {panel.kind === 'embed' ? (
                        panel.content
                    ) : (
                        <Suspense fallback={null}>
                            <DynamicForm
                                form={panel.form}
                                privacyPolicyPage={privacyPolicyPage}
                                header={panel.header}
                                successText={panel.successText}
                                errorText={panel.errorText}
                                centered={centered}
                            />
                        </Suspense>
                    )}
                </div>
            ))}
        </div>
    );
}
